import { useState, useEffect } from 'react';

interface GeoData {
  country_code: string;
  country_name: string;
}

export default function GeoRedirectModal() {
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [countryName, setCountryName] = useState('');

  useEffect(() => {
    // Check if user has already seen the modal
    if (document.cookie.includes('geo_redirect=seen')) {
      return;
    }

    // Detect geo-location and show modal if needed
    detectGeoLocation();
  }, []);

  const detectGeoLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data: GeoData = await response.json();
      const country = data.country_code;
      const currentDomain = window.location.hostname;
      
      let shouldShowPopup = false;
      let popupMessage = '';
      let targetUrl = '';
      let detectedCountry = data.country_name || 'International';

      // Logic for elixirsue.ca - suggest .com for non-Canadian visitors
      if (currentDomain.includes('elixirsue.ca') && country !== 'CA') {
        shouldShowPopup = true;
        popupMessage = 'Welcome international visitor! For the best global experience with USD pricing and worldwide shipping options, visit elixirsue.com.';
        targetUrl = 'https://elixirsue.com' + window.location.pathname;
      } 
      // Logic for elixirsue.com - suggest .ca for Canadian visitors
      else if (currentDomain.includes('elixirsue.com') && country === 'CA') {
        shouldShowPopup = true;
        popupMessage = 'Hello Canada! Get Canada-specific content, CAD pricing, and optimized shipping at elixirsue.ca.';
        targetUrl = 'https://elixirsue.ca' + window.location.pathname;
      }

      if (shouldShowPopup) {
        setMessage(popupMessage);
        setRedirectUrl(targetUrl);
        setCountryName(detectedCountry);
        setShowModal(true);

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
          handleDismiss();
        }, 5000);
      }
    } catch (error) {
      console.log('Geo-detection unavailable, proceeding normally');
    }
  };

  const handleRedirect = () => {
    // Set cookie before redirecting
    setCookie();
    window.location.href = redirectUrl;
  };

  const handleDismiss = () => {
    setCookie();
    setShowModal(false);
  };

  const setCookie = () => {
    // Set cookie for 30 days
    const maxAge = 30 * 24 * 60 * 60; // 30 days in seconds
    document.cookie = `geo_redirect=seen; max-age=${maxAge}; path=/; SameSite=Strict; Secure`;
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70] p-4 animate-fade-in">
      <div className="bg-gradient-to-br from-[#2D5F3F] via-[#3A7A52] to-[#2D5F3F] text-white rounded-xl shadow-2xl max-w-md w-full p-8 relative animate-slide-down">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors cursor-pointer"
          aria-label="Close modal"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center">
            <i className="ri-global-line text-3xl text-white"></i>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-2xl font-bold text-center mb-3">
          Welcome{countryName ? ` from ${countryName}` : ''}!
        </h3>

        {/* Message */}
        <p className="text-white/90 text-center text-sm leading-relaxed mb-6">
          {message}
        </p>

        {/* Why this popup? */}
        <div className="bg-white/10 rounded-lg p-3 mb-6">
          <p className="text-xs text-white/80 text-center">
            <strong>Why this popup?</strong> We optimize content, pricing, and shipping based on your location to provide the best experience and comply with local regulations.
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleRedirect}
            className="flex-1 px-6 py-3 bg-[#D4AF37] text-[#2D5F3F] rounded-lg font-bold hover:bg-[#F5E6B3] transition-colors whitespace-nowrap cursor-pointer"
          >
            Yes, Switch Sites
          </button>
          <button
            onClick={handleDismiss}
            className="flex-1 px-6 py-3 bg-white text-[#2D5F3F] rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            No, Stay Here
          </button>
        </div>

        {/* Auto-dismiss notice */}
        <p className="text-xs text-white/60 text-center mt-4">
          This message will auto-dismiss in 5 seconds
        </p>
      </div>
    </div>
  );
}